import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import SpeakerView from '../components/IntroPresenter/SpeakerView';
import PublicView from '../components/IntroPresenter/PublicView';
import { db } from '../../../firebase/config';
import {
  doc, onSnapshot, collection, updateDoc, serverTimestamp,
  getDocs, writeBatch, deleteDoc, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import {
  Box, Typography, Button, Paper, IconButton, Chip,
  CircularProgress, Tooltip, Dialog, DialogContent,
  DialogTitle, DialogActions, TextField, Tab, Tabs, Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { QRCodeSVG } from 'qrcode.react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import TvIcon from '@mui/icons-material/Tv';
import TimerIcon from '@mui/icons-material/Timer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { WorkshopNLogo } from './WorkshopNAdmin';
import WordCloud from '../components/WordCloud';
import WorkshopAIAnalysis from '../components/WorkshopAIAnalysis';
import WorkshopPhotoCarousel from '../components/WorkshopPhotoCarousel';
import { useQRLogo, useCompanyLogo } from '../../../hooks/useQRLogo';
import { useAuth } from '../../../contexts/AuthContext';
import { WorkshopNHelpButton } from '../components/WorkshopNGuide';
import WorkshopNGuide from '../components/WorkshopNGuide';
import { authFetch } from '../../../utils/authFetch';

const MEDAL_COLORS = { gold: '#FFD700', silver: '#C0C0C0', bronze: '#CD7F32' };
const MEDAL_LABELS = { gold: 'Guld',   silver: 'Silver',  bronze: 'Brons' };
const MEDAL_CYCLE  = [null, 'gold', 'silver', 'bronze'];

function ResponseCard({ r, questionIndex, currentUserId, myStarsForQ, onStar, onCycleRank, onEdit, onDelete }) {
  const [hover, setHover] = useState(false);
  const stars = r.stars || [];
  const rank  = r.rank  || null;
  const mc    = rank ? MEDAL_COLORS[rank] : null;
  const ml    = rank ? MEDAL_LABELS[rank] : null;
  const starred = currentUserId && stars.includes(currentUserId);
  const canStar = starred || myStarsForQ < 3;

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        position: 'relative',
        p: '8px 10px', pt: '22px',
        borderRadius: '8px',
        bgcolor: rank ? mc + '12' : (hover ? 'rgba(255,255,255,0.04)' : 'transparent'),
        border: `1px solid ${rank ? mc + '44' : 'transparent'}`,
        borderLeft: `3px solid ${rank ? mc : 'transparent'}`,
        transition: 'all 0.15s',
        mb: 1.5,
      }}
    >
      {/* Medal badge */}
      {rank && (
        <Box sx={{ position: 'absolute', top: 4, right: 6, display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <EmojiEventsIcon sx={{ fontSize: 12, color: mc }} />
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: mc }}>{ml.toUpperCase()}</Typography>
        </Box>
      )}
      {/* Controls top bar */}
      <Box sx={{ position: 'absolute', top: 3, left: 6, right: rank ? 54 : 6,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.25, opacity: hover ? 1 : 0, transition: 'opacity 0.15s' }}>
          <IconButton size="small" onClick={() => onEdit(r)}
            sx={{ width: 16, height: 16, p: 0, color: 'text.disabled', '&:hover': { color: 'text.primary' } }}>
            <EditIcon sx={{ fontSize: 10 }} />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(r.docId)}
            sx={{ width: 16, height: 16, p: 0, color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
            <DeleteIcon sx={{ fontSize: 10 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title={rank ? `${ml} — klicka för att ändra` : 'Sätt medalj'}>
            <IconButton size="small" onClick={() => onCycleRank(r.docId, questionIndex, rank)}
              sx={{ width: 16, height: 16, p: 0, color: rank ? mc : 'text.disabled',
                opacity: rank ? 1 : (hover ? 0.6 : 0), transition: 'opacity 0.15s',
                '&:hover': { color: '#FFD700' } }}>
              <EmojiEventsIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </Tooltip>
          {stars.length > 0 && (
            <Typography fontSize="0.58rem" color="#FFD700" lineHeight={1}>{stars.length}</Typography>
          )}
          <IconButton size="small" onClick={() => canStar && onStar(r.docId, questionIndex)}
            sx={{ width: 16, height: 16, p: 0, color: starred ? '#FFD700' : 'text.disabled',
              cursor: canStar ? 'pointer' : 'not-allowed',
              '&:hover': { color: canStar ? '#FFD700' : 'text.disabled' } }}>
            {starred ? <StarIcon sx={{ fontSize: 11 }} /> : <StarBorderIcon sx={{ fontSize: 11 }} />}
          </IconButton>
        </Box>
      </Box>
      <Typography color="text.secondary" fontSize="0.7rem" mb={0.3}>{r.name}</Typography>
      <Typography color="text.primary" fontSize="0.9rem" sx={{ lineHeight: 1.5 }}>{r.answer}</Typography>
    </Box>
  );
}

const accent  = '#E05500';

function BoardTimerCircle({ label, totalSeconds, onClose }) {
  const [secs, setSecs] = useState(totalSeconds);
  const [running, setRunning] = useState(true);
  const intervalRef = useRef(null);

  const playAlarm = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [[0, 440], [0.25, 550], [0.5, 660], [0.75, 440], [1.0, 330]].forEach(([t, freq]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'square'; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.18, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.22);
        osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + 0.25);
      });
    } catch (_) {}
  };

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSecs(s => {
        if (s <= 1) { clearInterval(intervalRef.current); playAlarm(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  const pct = totalSeconds > 0 ? secs / totalSeconds : 0;
  const r = 68;
  const circ = 2 * Math.PI * r;
  const fillColor = secs <= 60 ? '#ef4444' : secs <= 120 ? '#FF9800' : accent;

  return (
    <Box sx={{
      position: 'absolute', top: '50%', left: '50%',
      transform: 'translate(-50%,-50%)',
      zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      <Box sx={{
        position: 'relative', width: 172, height: 172,
        background: 'rgba(8,8,8,0.88)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '50%',
        boxShadow: `0 0 40px ${fillColor}55, 0 0 0 2px ${fillColor}33`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <Box component="svg" width={172} height={172}
          sx={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}>
          <circle cx={86} cy={86} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={5} />
          <circle cx={86} cy={86} r={r} fill="none" stroke={fillColor} strokeWidth={5}
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
            style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 0.5s' }}
          />
        </Box>
        <TimerIcon sx={{ fontSize: 18, color: fillColor, mb: 0.5, zIndex: 1, opacity: 0.7 }} />
        <Typography sx={{
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          fontSize: '2.4rem', fontWeight: 900, letterSpacing: '-1px',
          color: fillColor, lineHeight: 1, zIndex: 1,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {mm}:{ss}
        </Typography>
        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.45)', mt: 0.5, zIndex: 1, letterSpacing: 0.8,
          textTransform: 'uppercase', maxWidth: 120, textAlign: 'center', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, zIndex: 1 }}>
          <IconButton size="small" onClick={() => setRunning(r => !r)}
            sx={{ width: 26, height: 26, bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
            {running
              ? <Box sx={{ width: 8, height: 8, bgcolor: 'currentColor', borderRadius: '1px' }} />
              : <PlayArrowIcon sx={{ fontSize: 14 }} />}
          </IconButton>
          <IconButton size="small" onClick={onClose}
            sx={{ width: 26, height: 26, bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: '#ef444422', color: '#ef4444' } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default function WorkshopNPresent() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const viewMode = searchParams.get('view');

  // Intro presenter views — lightweight, no auth required for public
  if (viewMode === 'public')  return <PublicView  sessionId={id} />;
  if (viewMode === 'speaker') return <SpeakerView sessionId={id} />;

  return <WorkshopNPresentMain />;
}

function WorkshopNPresentMain() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qrLogo = useQRLogo();
  const companyLogo = useCompanyLogo();
  const { currentUser } = useAuth();

  const [session,      setSession]      = useState(null);
  const [responses,    setResponses]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [currentQ,     setCurrentQ]     = useState(0);
  const [showQR,       setShowQR]       = useState(false);
  const [resetOpen,    setResetOpen]    = useState(false);
  const [resetting,    setResetting]    = useState(false);
  const [kioskOpen,    setKioskOpen]    = useState(false);
  const [kioskBg,      setKioskBg]      = useState('');
  const [kioskTab,     setKioskTab]     = useState(0);
  const [uQuery,       setUQuery]       = useState('');
  const [uResults,     setUResults]     = useState([]);
  const [uLoading,     setULoading]     = useState(false);
  const [uError,       setUError]       = useState('');
  const [activeTimer,  setActiveTimer]  = useState(null);
  const [showRanking,  setShowRanking]  = useState(false);
  const [editDialog,   setEditDialog]   = useState({ open: false, docId: null, questionIndex: null, text: '' });
  const [showGuide,    setShowGuide]    = useState(() => !localStorage.getItem('workshopn-guide-dismissed'));

  // Live session doc
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', id), snap => {
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() };
      setSession(data);
      setCurrentQ(data.status?.currentQuestion ?? 0);
      setLoading(false);
    });
    return unsub;
  }, [id]);

  // Live responses
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'workshops', id, 'responses'), snap => {
      setResponses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [id]);

  const updateStatus = (patch) =>
    updateDoc(doc(db, 'workshops', id), {
      status: { ...(session?.status || {}), ...patch },
      updatedAt: serverTimestamp(),
    });

  const goToQuestion = (n) => {
    setCurrentQ(n);
    updateStatus({ currentQuestion: n });
  };

  const toggleLock = () => updateStatus({ locked: !session?.status?.locked });
  const toggleActive = () => updateStatus({ active: !session?.status?.active });

  const handleReset = async () => {
    setResetting(true);
    try {
      const snap = await getDocs(collection(db, 'workshops', id, 'responses'));
      const batch = writeBatch(db);
      snap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
      setResetOpen(false);
    } finally {
      setResetting(false);
    }
  };

  // ── Per-svar åtgärder ──────────────────────────────────────────────────────────
  const toggleStar = async (docId, questionIndex) => {
    if (!uid) return;
    const ref = doc(db, 'workshops', id, 'responses', docId);
    const r = responses.find(x => x.id === docId);
    const stars = r?.answerStars?.[questionIndex] || [];
    if (stars.includes(uid)) {
      await updateDoc(ref, { [`answerStars.${questionIndex}`]: arrayRemove(uid) });
    } else {
      if (myStarsForCurrentQ >= 3) return;
      await updateDoc(ref, { [`answerStars.${questionIndex}`]: arrayUnion(uid) });
    }
  };

  const cycleRank = async (docId, questionIndex, currentRank) => {
    const idx = MEDAL_CYCLE.indexOf(currentRank);
    const next = MEDAL_CYCLE[(idx + 1) % MEDAL_CYCLE.length];
    await updateDoc(doc(db, 'workshops', id, 'responses', docId), {
      [`answerRanks.${questionIndex}`]: next || null,
    });
  };

  const computeRanksFromVotes = async (questionIndex) => {
    const qResponses = responses.filter(
      r => r.answers?.[questionIndex] !== undefined && r.answers?.[questionIndex] !== ''
    );
    const sorted = [...qResponses].sort(
      (a, b) => (b.answerStars?.[questionIndex]?.length || 0) - (a.answerStars?.[questionIndex]?.length || 0)
    );
    const batch = writeBatch(db);
    sorted.forEach((r, i) => {
      const rank = i === 0 && (r.answerStars?.[questionIndex]?.length || 0) > 0 ? 'gold'
        : i === 1 && (r.answerStars?.[questionIndex]?.length || 0) > 0 ? 'silver'
        : i === 2 && (r.answerStars?.[questionIndex]?.length || 0) > 0 ? 'bronze'
        : null;
      batch.update(doc(db, 'workshops', id, 'responses', r.id), {
        [`answerRanks.${questionIndex}`]: rank || null,
      });
    });
    await batch.commit();
  };

  const clearRanks = async (questionIndex) => {
    const batch = writeBatch(db);
    responses.forEach(r => {
      batch.update(doc(db, 'workshops', id, 'responses', r.id), {
        [`answerRanks.${questionIndex}`]: null,
      });
    });
    await batch.commit();
  };

  const deleteResponse = async (docId) => {
    await deleteDoc(doc(db, 'workshops', id, 'responses', docId));
  };

  const saveEdit = async () => {
    const { docId, questionIndex, text } = editDialog;
    if (!text.trim() || !docId) return;
    await updateDoc(doc(db, 'workshops', id, 'responses', docId), {
      [`answers.${questionIndex}`]: text.trim(),
    });
    setEditDialog({ open: false, docId: null, questionIndex: null, text: '' });
  };

  const searchUnsplash = async () => {
    if (!uQuery.trim()) return;
    setULoading(true); setUError(''); setUResults([]);
    try {
      const res = await authFetch(`/.netlify/functions/unsplash?q=${encodeURIComponent(uQuery)}&per_page=12`);
      const data = await res.json();
      if (data.error) { setUError(`Fel: ${data.error}`); }
      else { setUResults(data.results || []); }
    } catch { setUError('Kunde inte nå Unsplash.'); }
    finally { setULoading(false); }
  };

  const launchKiosk = () => {
    let bgRef = '';
    if (kioskBg) {
      if (kioskBg === session?.imageUrl) {
        // Workshopbild finns redan i session — skicka hela URL (kort)
        bgRef = `?bg=${encodeURIComponent(kioskBg)}`;
      } else {
        // Unsplash-URL kan vara lång — lagra i localStorage, skicka nyckel
        localStorage.setItem('workshopKioskBg', kioskBg);
        bgRef = '?bg=custom';
      }
    }
    window.open(`/workshopn/${id}/kiosk${bgRef}`, '_blank');
    setKioskOpen(false);
  };

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  const questions = session?.config?.questions || [];
  const groups    = session?.config?.groups    || [];
  const question  = questions[currentQ];
  const isLocked  = session?.status?.locked;
  const isActive  = session?.status?.active;
  const totalResponses = responses.length;

  // Responses for current question, indexed by group
  const responsesByGroup = {};
  const participantsByGroup = {};

  // How many stars the current user has given for this question (across all groups)
  const uid = currentUser?.uid;
  const myStarsForCurrentQ = uid
    ? responses.filter(r => (r.answerStars?.[currentQ] || []).includes(uid)).length
    : 0;

  for (const g of groups) {
    responsesByGroup[g.id] = responses
      .filter(r => r.groupId === g.id && r.answers?.[currentQ] !== undefined && r.answers?.[currentQ] !== '')
      .map(r => ({
        docId:  r.id,
        name:   r.displayName,
        answer: r.answers[currentQ],
        stars:  r.answerStars?.[currentQ] || [],
        rank:   r.answerRanks?.[currentQ]  || null,
      }));
    // Collect unique participant names from two sources:
    // 1. Hybrid scans — names written in DELTAGARE-section on sheet
    const hybridNames = responses
      .filter(r => r.groupId === g.id && Array.isArray(r.participantNames))
      .flatMap(r => r.participantNames)
      .filter(n => n?.trim());
    // 2. Digital participants — displayName on their responses (deduplicated by userId)
    const seenUids = new Set();
    const digitalNames = responses
      .filter(r => r.groupId === g.id && !r.isHybridScan && r.userId && r.displayName?.trim())
      .filter(r => { if (seenUids.has(r.userId)) return false; seenUids.add(r.userId); return true; })
      .map(r => r.displayName);
    participantsByGroup[g.id] = [...new Set([...hybridNames, ...digitalNames])];
  }

  const participantUrl = `${window.location.origin}/workshop/${id}`;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', display: 'flex', flexDirection: 'column', position: 'relative' }}>

      {/* Hero — foton eller omslagsbild/video */}
      {(session?.photos || []).length > 0 ? (
        <WorkshopPhotoCarousel
          photos={session.photos}
          height="clamp(200px, 38vw, 420px)"
          autoPlay
          objectFit="cover"
          borderRadius={0}
        />
      ) : (session?.videoUrl || session?.imageUrl) ? (
        <Box sx={{ position: 'relative', width: '100%', height: 'clamp(160px, 30vw, 320px)', flexShrink: 0, overflow: 'hidden' }}>
          {session?.videoUrl ? (
            <Box component="video" src={session.videoUrl} autoPlay muted loop playsInline
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Box component="img" src={session.imageUrl} alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}
          {/* Dubbelgradient: mörk topp + mörk botten, klar mitt */}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.68) 0%, transparent 36%, transparent 54%, rgba(0,0,0,0.80) 100%)',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            px: 3, pt: 2, pb: 2.5,
          }}>
            {/* Topp: workshoptitel + logga */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{
                color: 'rgba(255,255,255,0.88)', fontWeight: 600,
                fontSize: 'clamp(0.78rem, 1.4vw, 1rem)',
                letterSpacing: '0.01em',
                textShadow: '0 1px 6px rgba(0,0,0,0.7)',
              }}>
                {session?.title}
              </Typography>
              {companyLogo && (
                <Box component="img" src={companyLogo} alt=""
                  sx={{ height: 'clamp(16px, 2vw, 26px)', maxWidth: 100, objectFit: 'contain',
                    opacity: 0.9, flexShrink: 0,
                    filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.6))' }} />
              )}
            </Box>
            {/* Botten: aktiv fråga */}
            {question && (
              <Box>
                <Typography sx={{
                  color: 'rgba(255,255,255,0.5)', fontSize: 'clamp(0.62rem, 0.9vw, 0.78rem)',
                  textShadow: '0 1px 4px rgba(0,0,0,0.8)', mb: 0.5,
                }}>
                  Fråga {currentQ + 1} av {questions.length}
                </Typography>
                <Typography sx={{
                  color: '#fff', fontWeight: 800,
                  fontSize: 'clamp(1rem, 2.8vw, 2.2rem)',
                  lineHeight: 1.2, letterSpacing: '-0.2px',
                  textShadow: '0 2px 14px rgba(0,0,0,0.7)',
                }}>
                  {question.text}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ) : null}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <IconButton onClick={() => navigate(`/workshopn/${id}/edit`)} sx={{ color: 'text.secondary' }} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mr: 1 }}>
          <PeopleIcon fontSize="small" />
          <Typography fontSize="0.82rem">{totalResponses} svar</Typography>
        </Box>

        <Chip
          label={isActive ? 'Aktiv' : 'Inaktiv'}
          size="small"
          onClick={toggleActive}
          sx={{
            bgcolor: isActive ? '#4ade8022' : 'action.selected',
            color: isActive ? '#4ade80' : 'text.secondary',
            cursor: 'pointer', fontWeight: 600, fontSize: '0.7rem',
          }}
        />
        <WorkshopNHelpButton />
        <Tooltip title="Visa QR-kod (för sena deltagare)">
          <IconButton onClick={() => setShowQR(true)} sx={{ color: 'text.secondary' }} size="small">
            <QrCodeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={isLocked ? 'Lås upp svar' : 'Lås svar'}>
          <IconButton onClick={toggleLock} sx={{ color: isLocked ? '#ef4444' : 'text.secondary' }} size="small">
            {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Kiosk-läge (helskärm)">
          <IconButton onClick={() => { setKioskBg(session?.videoUrl ? '' : (session?.imageUrl || '')); setKioskOpen(true); }} sx={{ color: 'text.secondary', '&:hover': { color: accent } }} size="small">
            <TvIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Redigera">
          <IconButton onClick={() => navigate(`/workshopn/${id}/edit`)} sx={{ color: 'text.secondary' }} size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rankning &amp; röstning">
          <IconButton onClick={() => setShowRanking(r => !r)}
            sx={{ color: showRanking ? '#FFD700' : 'text.secondary', bgcolor: showRanking ? '#FFD70022' : 'transparent', borderRadius: 1.5 }}
            size="small">
            <EmojiEventsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Nollställ alla svar">
          <IconButton onClick={() => setResetOpen(true)} sx={{ color: 'text.secondary', '&:hover': { color: '#ef4444' } }} size="small">
            <DeleteSweepIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Frågnavigation */}
      <Box sx={{ display: 'flex', gap: 1, px: 3, py: 1.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap', flexShrink: 0 }}>
        {questions.map((q, i) => (
          <Button
            key={i}
            size="small"
            onClick={() => goToQuestion(i)}
            variant={i === currentQ ? 'contained' : 'outlined'}
            sx={{
              textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 1.5,
              bgcolor: i === currentQ ? accent : 'transparent',
              borderColor: i === currentQ ? accent : '#444',
              color: 'text.primary',
              '&:hover': { bgcolor: accent + '33', borderColor: accent },
            }}
          >
            {i + 1}. {q.text.length > 28 ? q.text.slice(0, 28) + '…' : q.text}
          </Button>
        ))}
      </Box>

      {/* Fasilitatorsguide — dismissible */}
      <Collapse in={showGuide}>
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2, pb: 0.5 }}>
            <Typography fontSize="0.7rem" fontWeight={700} color="text.disabled" letterSpacing={1}>
              FASILITATORSGUIDE
            </Typography>
            <Tooltip title="Stäng — öppna igen via ? ikonen i verktygsfältet">
              <IconButton size="small"
                onClick={() => { setShowGuide(false); localStorage.setItem('workshopn-guide-dismissed', '1'); }}
                sx={{ color: 'text.disabled' }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <WorkshopNGuide />
        </Box>
      </Collapse>

      {/* Huvudinnehåll */}
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, overflow: 'auto' }}>
        {/* Fråga */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography color="text.disabled" fontSize="0.78rem" mb={0.5}>
              Fråga {currentQ + 1} av {questions.length}
            </Typography>
            <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1.15, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: accent }}>
              {question?.text}
            </Typography>
            {question?.hint && (
              <Typography color="text.secondary" mt={1.5} fontSize="1.1rem" sx={{ lineHeight: 1.6 }}>{question.hint}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
            {/* Timer-knapp för aktuell fråga */}
            <Tooltip title="Starta timer för den här frågan">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1.25, py: 0.5, borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'action.hover', cursor: 'pointer',
                '&:hover': { borderColor: accent, bgcolor: accent + '0d' },
              }} onClick={() => {
                const secs = session?.timerDurations?.[currentQ] ?? 300;
                setActiveTimer({ questionIndex: currentQ, totalSeconds: secs });
                // Sync timer to HDMI display
                updateDoc(doc(db, 'workshops', id), {
                  'display.timer': {
                    endsAt: Date.now() + secs * 1000,
                    totalSeconds: secs,
                    label: questions[currentQ]?.text || `Fråga ${currentQ + 1}`,
                  },
                });
              }}>
                <TimerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography fontSize="0.72rem" color="text.secondary" fontWeight={600}>
                  {Math.round((session?.timerDurations?.[currentQ] ?? 300) / 60)} min
                </Typography>
                <PlayArrowIcon sx={{ fontSize: 14, color: accent }} />
              </Box>
            </Tooltip>
            <Box
              component="input"
              type="number" min={1} max={60}
              defaultValue={Math.round((session?.timerDurations?.[currentQ] ?? 300) / 60)}
              key={`timer-q${currentQ}`}
              title="Ändra tid (minuter)"
              onBlur={e => {
                const v = Math.max(1, Math.min(60, parseInt(e.target.value) || 5));
                updateDoc(doc(db, 'workshops', id), {
                  [`timerDurations.${currentQ}`]: v * 60,
                });
              }}
              style={{
                width: 40, textAlign: 'center', fontSize: '0.8rem',
                background: 'transparent',
                border: '1px solid rgba(0,0,0,0.15)',
                borderRadius: 6, color: 'inherit', padding: '4px 0',
                outline: 'none',
              }}
            />
            <IconButton
              onClick={() => goToQuestion(currentQ - 1)}
              disabled={currentQ === 0}
              sx={{ color: 'text.secondary', '&.Mui-disabled': { color: 'text.disabled' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={() => goToQuestion(currentQ + 1)}
              disabled={currentQ === questions.length - 1}
              sx={{ color: 'text.secondary', '&.Mui-disabled': { color: 'text.disabled' } }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Hybrid scan-knappar */}
        {session?.mode === 'hybrid' && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#E0550011', border: '1px solid #E0550033', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <DocumentScannerIcon sx={{ color: accent, fontSize: 18 }} />
              <Typography fontSize="0.82rem" fontWeight={700} color={accent}>
                Scanna hybridblad — Fråga {currentQ + 1}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {groups.map(g => (
                <Button
                  key={g.id}
                  size="small"
                  variant="outlined"
                  startIcon={<DocumentScannerIcon />}
                  onClick={() => window.open(
                    `/workshop-scan?s=${id}&q=${currentQ}&g=${g.id}`,
                    '_blank'
                  )}
                  sx={{
                    borderColor: g.color || accent,
                    color: g.color || accent,
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    '&:hover': { bgcolor: (g.color || accent) + '22' },
                  }}
                >
                  {g.name}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {/* Gruppsvar */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${Math.min(groups.length, 3)}, 1fr)` },
          gap: 2,
        }}>
          {groups.map(g => {
            const groupResponses    = responsesByGroup[g.id]    || [];
            const groupParticipants = participantsByGroup[g.id] || [];
            return (
              <Paper key={g.id} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                {/* Grupphuvud */}
                <Box sx={{ px: 2, pt: 1.5, pb: groupParticipants.length ? 0.75 : 1.5, borderBottom: `5px solid ${g.color || accent}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.color || accent, flexShrink: 0 }} />
                    <Typography fontWeight={700} sx={{ flex: 1 }}>{g.name}</Typography>
                    <Typography color="text.disabled" fontSize="0.75rem">
                      {groupResponses.length} svar
                    </Typography>
                    <Tooltip title="Visa den här gruppens svar på HDMI-skärmen">
                      <IconButton size="small"
                        onClick={() => updateDoc(doc(db, 'workshops', id), {
                          'display.mode': 'group-responses',
                          'display.groupId': g.id,
                        })}
                        sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: g.color || accent } }}>
                        <TvIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {groupParticipants.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                      {groupParticipants.map((name, i) => (
                        <Typography key={i} fontSize="0.68rem" color="text.disabled"
                          sx={{ bgcolor: 'action.hover', borderRadius: 0.75, px: 0.75, py: 0.2 }}>
                          {name}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Svar */}
                <Box sx={{ p: 2, minHeight: 100 }}>
                  {groupResponses.length === 0 ? (
                    <Typography color="#555" fontSize="0.82rem" fontStyle="italic">
                      Inga svar ännu...
                    </Typography>
                  ) : (
                    groupResponses.map((r) => (
                      <ResponseCard
                        key={r.docId}
                        r={r}
                        questionIndex={currentQ}
                        currentUserId={uid}
                        myStarsForQ={myStarsForCurrentQ}
                        onStar={toggleStar}
                        onCycleRank={cycleRank}
                        onEdit={(r) => setEditDialog({ open: true, docId: r.docId, questionIndex: currentQ, text: r.answer })}
                        onDelete={deleteResponse}
                      />
                    ))
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>

        {/* Rankning-panel */}
        {showRanking && (
          <Box sx={{ mt: 3, p: 2.5, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 18 }} />
              <Typography fontWeight={700} fontSize="0.9rem" sx={{ flex: 1 }}>
                Rankning — Fråga {currentQ + 1}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<AutoAwesomeIcon />}
                onClick={() => computeRanksFromVotes(currentQ)}
                disabled={!responses.some(r => (r.answerStars?.[currentQ] || []).length > 0)}
                sx={{ textTransform: 'none', color: '#FFD700', borderColor: '#FFD70055', fontSize: '0.75rem',
                  '&:hover': { borderColor: '#FFD700', bgcolor: '#FFD70011' },
                  '&.Mui-disabled': { color: 'text.disabled', borderColor: 'divider' } }}>
                Beräkna från röster
              </Button>
              <Button size="small" onClick={() => clearRanks(currentQ)}
                sx={{ textTransform: 'none', color: 'text.disabled', fontSize: '0.72rem',
                  '&:hover': { color: '#ef4444' } }}>
                Rensa
              </Button>
            </Box>
            <Typography fontSize="0.72rem" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              Klicka på <EmojiEventsIcon sx={{ fontSize: 12, verticalAlign: 'middle', color: '#FFD700' }} /> på ett svar för att sätta guld/silver/brons manuellt.
              Eller låt deltagarna rösta med stjärnor (max 3 per person per fråga) och klicka "Beräkna från röster".
            </Typography>
          </Box>
        )}

        {/* Ordmoln — visas för ordmoln- och fritext-frågor */}
        {(question?.type === 'ordmoln' || question?.type === 'fritext') && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography color="text.secondary" fontSize="0.75rem" fontWeight={700} letterSpacing={1}>
                {question.type === 'ordmoln' ? 'ORDMOLN' : 'ORDMOLN — byggs upp i realtid'}
              </Typography>
              {question.type === 'fritext' && (
                <Typography fontSize="0.68rem" color="text.disabled">
                  (uppdateras automatiskt när nya svar skannas)
                </Typography>
              )}
            </Box>
            <WordCloud responses={responses} questionIndex={currentQ} showSettings />
          </Box>
        )}

        {/* AI-analys — per grupp (med HDMI-knapp) + samlad helgruppsanalys */}
        {groups.length > 1 ? (
          <Box sx={{ mt: 4 }}>
            {groups.map(g => (
              <WorkshopAIAnalysis
                key={g.id}
                session={session}
                responses={responses}
                groupId={g.id}
                groupName={g.name}
                onDisplayGroup={async (gId) => {
                  await updateDoc(doc(db, 'workshops', id), {
                    'display.mode': 'analysis',
                    'display.groupId': gId,
                  });
                }}
              />
            ))}
            <WorkshopAIAnalysis session={session} responses={responses} />
          </Box>
        ) : (
          <WorkshopAIAnalysis session={session} responses={responses} />
        )}

        {/* Fortsätt med SWOT */}
        <Box sx={{ mt: 3, p: 2.5, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
            <Box>
              <Typography fontWeight={700} fontSize="0.9rem" mb={0.3}>
                Gå vidare med SWOT-analys
              </Typography>
              <Typography color="text.secondary" fontSize="0.8rem">
                AI importerar workshopens anteckningar och sorterar dem i S/W/O/T automatiskt.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate(`/swotn/new?workshopId=${id}&title=${encodeURIComponent(session.title)}`)}
              sx={{ color: '#E05500', borderColor: '#E05500', textTransform: 'none', fontWeight: 600, flexShrink: 0,
                '&:hover': { bgcolor: '#E0550011' } }}
            >
              Skapa SWOT →
            </Button>
          </Box>
        </Box>

        {/* Deltagarlink */}
        <Box sx={{ mt: 4, p: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography color="text.secondary" fontSize="0.75rem" mb={0.5}>Deltagarlink</Typography>
          <Typography fontFamily="monospace" color="text.primary" fontSize="0.85rem">{participantUrl}</Typography>
        </Box>
      </Box>

      {/* Redigera svar */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, docId: null, questionIndex: null, text: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>Redigera svar</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth multiline rows={3} size="small"
            value={editDialog.text}
            onChange={e => setEditDialog(d => ({ ...d, text: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveEdit(); }}
            sx={{ mt: 1.5 }}
          />
          <Typography fontSize="0.68rem" color="text.disabled" mt={0.5}>Ctrl+Enter för att spara</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditDialog({ open: false, docId: null, questionIndex: null, text: '' })}
            sx={{ textTransform: 'none', color: 'text.secondary' }}>Avbryt</Button>
          <Button variant="contained" onClick={saveEdit} disabled={!editDialog.text.trim()}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}>
            Spara
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR-dialog för sena deltagare */}
      <Dialog open={showQR} onClose={() => setShowQR(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
          <WorkshopNLogo />
          <Typography fontWeight={700} textAlign="center">{session?.title}</Typography>
          <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 2 }}>
            <QRCodeSVG
              value={participantUrl} size={220}
              level="H"
              {...(qrLogo ? {
                imageSettings: { src: qrLogo, height: 48, width: 48, excavate: true },
              } : {})}
            />
          </Box>
          <Typography fontFamily="monospace" fontSize="0.8rem" color="text.secondary" textAlign="center">
            {participantUrl}
          </Typography>
          <Button onClick={() => setShowQR(false)} sx={{ color: accent, textTransform: 'none' }}>
            Stäng
          </Button>
        </DialogContent>
      </Dialog>

      {/* Nollställ-dialog */}
      <Dialog open={resetOpen} onClose={() => !resetting && setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DeleteSweepIcon sx={{ color: '#ef4444' }} />
          Nollställ alla svar
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" fontSize="0.9rem">
            Alla insamlade svar för <strong>{session?.title}</strong> raderas permanent.
            Frågor och grupper behålls — bara svaren tas bort.
          </Typography>
          <Typography color="#ef4444" fontSize="0.82rem" mt={1.5} fontWeight={600}>
            Det går inte att ångra.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setResetOpen(false)} disabled={resetting} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={handleReset}
            disabled={resetting}
            startIcon={resetting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DeleteSweepIcon />}
            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none', fontWeight: 700 }}
          >
            {resetting ? 'Raderar...' : 'Nollställ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kiosk-bakgrundsbildsdialog */}
      <Dialog open={kioskOpen} onClose={() => setKioskOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TvIcon sx={{ color: accent }} />
          Kiosk-läge — välj bakgrundsbild
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {(() => {
            const hasHeroMedia = !!(session?.imageUrl || session?.videoUrl);
            return (
              <Tabs value={kioskTab} onChange={(_, v) => setKioskTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                {hasHeroMedia && <Tab label={session?.videoUrl ? 'Workshopvideo' : 'Workshopbild'} sx={{ textTransform: 'none', fontSize: '0.82rem' }} />}
                <Tab label="Unsplash" sx={{ textTransform: 'none', fontSize: '0.82rem' }} />
                <Tab label="Ingen bakgrund" sx={{ textTransform: 'none', fontSize: '0.82rem' }} />
              </Tabs>
            );
          })()}

          {/* Tab: Workshop-media (bild eller video) */}
          {kioskTab === 0 && !!(session?.imageUrl || session?.videoUrl) && (() => {
            const hasVideo = !!session?.videoUrl;
            const heroSelected = hasVideo ? kioskBg === '' : kioskBg === session?.imageUrl;
            return (
              <Box
                onClick={() => setKioskBg(hasVideo ? '' : session.imageUrl)}
                sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', cursor: 'pointer', border: heroSelected ? `3px solid ${accent}` : '3px solid transparent' }}
              >
                {session?.imageUrl ? (
                  <Box component="img" src={session.imageUrl} alt="" sx={{ width: '100%', maxHeight: 240, objectFit: 'cover', display: 'block' }} />
                ) : (
                  <Box component="video" src={session.videoUrl} preload="metadata" muted playsInline
                    sx={{ width: '100%', maxHeight: 240, objectFit: 'cover', display: 'block' }} />
                )}
                {hasVideo && (
                  <Chip label="Spelas som video" size="small"
                    sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.72)', color: '#fff', fontSize: '0.68rem' }} />
                )}
                {heroSelected && (
                  <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, color: accent, bgcolor: '#fff', borderRadius: '50%', fontSize: 24 }} />
                )}
              </Box>
            );
          })()}

          {/* Tab: Unsplash */}
          {((!!(session?.imageUrl || session?.videoUrl) && kioskTab === 1) || (!(session?.imageUrl || session?.videoUrl) && kioskTab === 0)) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small" fullWidth
                  placeholder="Sök t.ex. office, nature, team..."
                  value={uQuery}
                  onChange={e => setUQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchUnsplash()}
                />
                <Button variant="outlined" onClick={searchUnsplash} disabled={uLoading}
                  startIcon={uLoading ? <CircularProgress size={14} /> : <SearchIcon />}
                  sx={{ borderColor: accent, color: accent, textTransform: 'none', whiteSpace: 'nowrap' }}>
                  Sök
                </Button>
              </Box>
              {uError && <Typography color="error" fontSize="0.8rem">{uError}</Typography>}
              {uResults.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 0.75 }}>
                  {uResults.map(img => (
                    <Box key={img.id} onClick={() => setKioskBg(img.regular)}
                      sx={{ position: 'relative', aspectRatio: '16/9', borderRadius: 1, overflow: 'hidden', cursor: 'pointer',
                        border: kioskBg === img.regular ? `3px solid ${accent}` : '3px solid transparent',
                        '&:hover': { transform: 'scale(1.03)' }, transition: 'all 0.15s' }}>
                      <Box component="img" src={img.thumb} alt={img.alt} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {kioskBg === img.regular && (
                        <CheckCircleIcon sx={{ position: 'absolute', top: 4, right: 4, color: accent, bgcolor: '#fff', borderRadius: '50%', fontSize: 18 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {!uResults.length && !uLoading && !uError && (
                <Typography fontSize="0.78rem" color="text.disabled" textAlign="center" py={2}>
                  Sök efter gratis licensfria bilder från Unsplash
                </Typography>
              )}
            </Box>
          )}

          {/* Tab: Ingen bakgrund */}
          {((!!(session?.imageUrl || session?.videoUrl) && kioskTab === 2) || (!(session?.imageUrl || session?.videoUrl) && kioskTab === 1)) && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Button
                variant={kioskBg === '' ? 'contained' : 'outlined'}
                onClick={() => setKioskBg('')}
                sx={{ textTransform: 'none', bgcolor: kioskBg === '' ? accent : 'transparent', borderColor: accent, color: kioskBg === '' ? '#fff' : accent }}
              >
                Mörk bakgrund utan bild
              </Button>
            </Box>
          )}

          {/* Förhandsgranskning */}
          {kioskBg && (
            <Box sx={{ mt: 2, position: 'relative', borderRadius: 1.5, overflow: 'hidden', height: 80 }}>
              <Box component="img" src={kioskBg} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(6px) brightness(0.4)', transform: 'scale(1.1)' }} />
              <Typography sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                Förhandsgranskning
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setKioskOpen(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Avbryt</Button>
          <Button variant="contained" onClick={launchKiosk} startIcon={<TvIcon />}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 700 }}>
            Öppna kiosk
          </Button>
        </DialogActions>
      </Dialog>

      {/* In-board timer circle */}
      {activeTimer && (
        <BoardTimerCircle
          key={`${activeTimer.questionIndex}-${activeTimer.totalSeconds}`}
          label={questions[activeTimer.questionIndex]?.text || `Fråga ${activeTimer.questionIndex + 1}`}
          totalSeconds={activeTimer.totalSeconds}
          onClose={() => {
            setActiveTimer(null);
            updateDoc(doc(db, 'workshops', id), { 'display.timer': null });
          }}
        />
      )}
    </Box>
  );
}
